<!--
  SupportNewTicket.svelte — the customer opens a ticket.

  Only products the user is currently eligible for are offered: the server would
  refuse the others anyway, and a picker that lists them only to fail on submit
  wastes the time of someone who already has a problem. A user with no eligible
  product is sent back to the overview, which explains why for each product.

  `?followUp=LOD-1042` carries the closed ticket this one continues, which is the
  route the customer ticket view offers instead of replying to a closed ticket.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import { EmptyState, PageHeader, RoutedButton, SupportCommunityHelp, SupportTicketForm } from '../../components';
	import { createSupportTicket, getSupportOverview } from '../../AppNetwork';
	import type { SupportCommunityLinks, SupportProduct, SupportTicketInput } from '../../AppTypes';
	import { push, querystring } from '../../abstract-spa-router';
	import { toastError, toastSuccess } from '../../toast';
	import { t, t2 } from '../../i18n';

	interface Props {
		/** Route params — `appUUID` preselects a product. */
		params?: { appUUID?: string };
	}

	let { params = {} }: Props = $props();

	/** Products the user may file against right now. */
	let products: SupportProduct[] = $state([]);
	/** Whether the support desk is switched on. */
	let isEnabled = $state(true);
	/** Where to point a user with no eligible product. */
	let communityLinks = $state<SupportCommunityLinks | null>(null);
	/** Whether the overview is loading. */
	let loading = $state(true);
	/** Failure of the overview. */
	let error = $state('');
	/** Whether the ticket is being submitted. */
	let submitting = $state(false);
	/** Fraction of the attachments uploaded, while a submit with files is in flight; `null` otherwise. */
	let uploadProgress = $state<number | null>(null);

	/** The closed ticket this one follows up on, from the query string. */
	let followUpOfKey = $derived(new URLSearchParams($querystring || '').get('followUp') ?? '');

	/** Loads the products the user is eligible for. */
	async function load(): Promise<void> {
		loading = true;
		error = '';
		/** Eligibility is live, so it is read on every visit rather than carried from the overview page. */
		try {
			const overview = await getSupportOverview();
			products = overview.products.filter((product) => product.eligibility?.isSupportAvailable === true);
			communityLinks = overview.communityLinks;
			isEnabled = overview.isEnabled !== false;
		} catch (e) {
			error = e instanceof Error ? e.message : t2('Failed to load support');
		} finally {
			loading = false;
		}
	}

	/** Files the ticket and opens it. */
	async function submit(input: SupportTicketInput, files: File[]): Promise<void> {
		submitting = true;
		/* Only an upload with files shows a bar; without, the button's spinner is enough. */
		uploadProgress = files.length > 0 ? 0 : null;
		const onProgress = files.length > 0 ? (fraction: number) => (uploadProgress = fraction) : undefined;
		/** The server re-checks eligibility; a refusal comes back as the message to show. */
		try {
			const ticket = await createSupportTicket(input, files, onProgress);
			toastSuccess(t2('Ticket {{key}} opened.', { key: ticket.ticketKey }));
			push(`/Support/Tickets/${ticket.ticketKey}`);
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to open the ticket'));
		} finally {
			submitting = false;
			uploadProgress = null;
		}
	}

	onMount(load);
</script>

<PageHeader
	title={$t('New ticket')}
	description={$t('Tell us what is going wrong. The more detail, the faster we can help.')}
	breadcrumbs={[{ label: $t('Support'), href: '/Support' }, { label: $t('New ticket') }]}
/>

{#if loading}
	<EmptyState variant="loading" />
{:else if error}
	<EmptyState variant="error" description={error} />
{:else if products.length === 0}
	<EmptyState
		faIcon="life-ring"
		title={isEnabled ? $t('No product offers you support right now') : $t('Support is currently turned off')}
		description={isEnabled
			? $t('Support comes with an active license. The support page explains the situation for each product.')
			: $t('Tickets cannot be opened or answered right now. Please check back later.')}
	>
		<RoutedButton size="sm" href="/Support">{$t('Back to support')}</RoutedButton>
	</EmptyState>
	<SupportCommunityHelp links={communityLinks} class="mt-4" />
{:else}
	<AbstractCard class="max-w-none">
		<SupportTicketForm
			{products}
			initialAppUUID={params.appUUID ?? ''}
			{followUpOfKey}
			{submitting}
			{uploadProgress}
			cancelHref="/Support"
			onsubmit={submit}
		/>
	</AbstractCard>
{/if}
